import NewDraggableProductList from "@/components/_Organisms/NewDraggableProductList";
import TabsTemplate from "@/components/_Templates/TabsTemplate";
import { useRunCollection, useSetState, useThrottle } from "@/hooks";
import { PreviewViewSettings } from "@/includes";
import { RootState } from "@/services/redux/store";
import { Stack } from "@mui/material";
import { useSelector as useXStateSelector } from "@xstate/react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useConfigureAlgorithm } from "../../../../../hooks/useConfigureAlgorithm";
import { Interpret } from "../../Interpret";
import { OLD_MERCH_TAB_ENUM } from "./enums";
import { IRunAndManipulate } from "./types";
import MerchArray from "../MerchArray";
import DraggablepinProductList from "@/components/_Organisms/DraggablePinProductList";
import NoRowsOverlay from "@/components/_Organisms/DraggableProductList/components/NoRowsOverlay";

const contextSelector = (state: any) => state.context;

// /!\ EMERCH ONLY
// todo refonte
const RunAndManipulate: React.FC<IRunAndManipulate> = ({ slotProps }) => {
  const interpreter = new Interpret();
  const { selectedCategory } = slotProps;

  const { t }: i18translateType = useTranslation();
  const siteId = useSelector((state: RootState) => state.site.siteId);
  const configureAlgorithmServices = useConfigureAlgorithm();

  const [params, setParams] = useSetState({});

  const { send } = configureAlgorithmServices.algorithmService;

  const context = useXStateSelector(
    configureAlgorithmServices.algorithmService,
    contextSelector
  );

  const { pins, blocks } = context;

  const categoryPins: Dic<string> = pins?.[selectedCategory.id] ?? {};

  const { settings }: Dic<any> = useMemo(
    () => interpreter.transformObjectIntoConditionalRule(context),
    [context]
  );

  const requiredParameters = settings.required_parameters ?? {};

  const { newHandleRun, runningCollection, collectionResult } =
    useRunCollection(siteId);

  const throtteledParams = useThrottle(params);
  const throtteledSettings = useThrottle(settings);

  useEffect(() => {
    if (throtteledSettings?.blocks?.length > 0) {
      newHandleRun(throtteledSettings, requiredParameters, throtteledParams);
    }
  }, [throtteledSettings, throtteledParams]);

  useEffect(() => {
    if (!!selectedCategory.id)
      setParams({ ...params, category_id: selectedCategory?.id });
  }, [selectedCategory]);

  const handleChangePins = (newCategoryPins: Dic<ID>) => {
    const newPins = { ...pins };
    newPins[selectedCategory.id] = { ...newCategoryPins };
    send({ type: "SET_PINS", data: { pins: newPins } });
  };

  const pushProductTo = (
    productId: ID,
    position: number,
    newPinsForCategory: Dic<ID> | null = null
  ): any => {
    const newCategoryPins = newPinsForCategory ?? categoryPins;

    const pushedProducts = Object.fromEntries(
      Object.entries(newCategoryPins ? { ...newCategoryPins } : {}).filter(
        (pin: Array<any>) => pin[1] !== productId
      )
    );

    if (pushedProducts[position] && pushedProducts[position] !== productId) {
      const currentlyPushed = pushedProducts[position];
      pushedProducts[position] = productId;
      return pushProductTo(currentlyPushed, position + 1, pushedProducts);
    } else {
      pushedProducts[position] = productId;
    }
    handleChangePins(pushedProducts);
  };

  const products = useMemo(
    () =>
      collectionResult.map((runResult: Array<Dic<any>>, index: number) => ({
        ...runResult,
        isPinned: !!categoryPins[index + 1],
      })),
    [collectionResult]
  );

  return (
    <>
      <Stack direction="row" justifyContent="end" alignItems="center" my={2}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <PreviewViewSettings />
        </Stack>
      </Stack>

      <TabsTemplate tabs={Object.values(OLD_MERCH_TAB_ENUM)}>
        {products.length === 0 && !runningCollection ? (
          <NoRowsOverlay list="run result" />
        ) : (
          <DraggablepinProductList
            items={products}
            pinnedItems={categoryPins}
            setPinnedItems={handleChangePins}
            itemsPerRow={4}
            categoryId={selectedCategory?.id ?? ""}
            forceReload={() => {}}
            fieldsSelected={[]}
          />
        )}
        <MerchArray
          runningCollection={runningCollection}
          selectedCategory={selectedCategory}
          products={products}
        />
      </TabsTemplate>
    </>
  );
};

export default RunAndManipulate;
